import * as Schema from "effect/Schema";
import { TrimmedNonEmptyString } from "./baseSchemas.ts";
import { MessageContextResolution } from "./contextResolution.ts";
import { OfflineContextReceipt, OfflineCanvas } from "./offlineWorld.ts";
import {
  ApprovalRequestId,
  EventId,
  IsoDateTime,
  MessageId,
  ProviderItemId,
  ThreadId,
  TurnId,
} from "./baseSchemas.ts";
import {
  ChatAttachment,
  LiveHandoff,
  ModelSelection,
  LocalWorkPolicy,
  PersonAuthorship,
  PROVIDER_SEND_TURN_MAX_ATTACHMENTS,
  PROVIDER_HANDOFF_MAX_TEXT_CHARS,
  PROVIDER_SEND_TURN_MAX_INPUT_CHARS,
  ProviderApprovalDecision,
  ProviderApprovalPolicy,
  ProviderInteractionMode,
  ProviderRequestKind,
  ProviderSandboxMode,
  ProviderUserInputAnswers,
  RuntimeMode,
} from "./orchestration.ts";
import { ProviderInstanceId, ProviderDriverKind } from "./providerInstance.ts";

/**
 * What an engine does with a message handed to it while a turn is running:
 * `native` reads it inside the running turn without stopping its tools,
 * `interrupt` needs the turn stopped first, `none` cannot take it at all.
 * Declared by the adapter, never inferred from the driver kind.
 */
export const ProviderMidRunDelivery = Schema.Literals(["native", "interrupt", "none"]);
export type ProviderMidRunDelivery = typeof ProviderMidRunDelivery.Type;

const ProviderSessionStatus = Schema.Literals([
  "connecting",
  "ready",
  "running",
  "error",
  "closed",
]);

export const ProviderSession = Schema.Struct({
  provider: ProviderDriverKind,
  // Optional during the driver/instance migration. Once every producer
  // populates it (post-slice-4), routing flips to instance-id-only and the
  // legacy `provider` field is removed.
  providerInstanceId: Schema.optional(ProviderInstanceId),
  status: ProviderSessionStatus,
  runtimeMode: RuntimeMode,
  cwd: Schema.optional(TrimmedNonEmptyString),
  model: Schema.optional(TrimmedNonEmptyString),
  threadId: ThreadId,
  resumeCursor: Schema.optional(Schema.Unknown),
  activeTurnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  lastError: Schema.optional(TrimmedNonEmptyString),
});
export type ProviderSession = typeof ProviderSession.Type;

/**
 * The provider operating mode, derived from ONE place (F04): a thread's
 * persisted World identity. `null`/absent worldKey IS the explicit Local
 * (ungoverned) harness mode — threads are stamped at birth by the active
 * door and rootSessionBirth refuses incoherent pairs — and a present key is
 * governed World mode. Adapters must branch on this resolver and never on a
 * process-wide flag or their own nullness checks.
 */
export type ProviderOperatingMode = "local" | "governed-world";
export const resolveProviderOperatingMode = (
  worldKey: string | null | undefined,
): ProviderOperatingMode =>
  typeof worldKey === "string" && worldKey.trim().length > 0 ? "governed-world" : "local";

/**
 * The session's audience for layered session injection, decided structurally
 * at birth and never stored: a parentless level-0 seat (the seat the Cleo
 * Panel displays) is `cleo`; a spawned descendant is a `hand`.
 */
export const ProviderSessionInjectionAudience = Schema.Literals(["cleo", "hand"]);
export type ProviderSessionInjectionAudience = typeof ProviderSessionInjectionAudience.Type;

/**
 * The composed session-injection payload, frozen onto the session start
 * input by the reactor (compiled by the World authority through the grant
 * and delivered verbatim behind the presentation contract; nothing local). Adapters deliver `text` verbatim on their harness's native
 * instruction channel — Codex inside developer instructions, Claude as the
 * claude_code preset's append, and the AI SDK lane as its system text.
 * `digest` (sha256 of `text`) and `layers` (the composed
 * layer keys, in order) are the delivery receipt for spans and logs.
 */
export const ProviderSessionInjection = Schema.Struct({
  audience: ProviderSessionInjectionAudience,
  text: Schema.String,
  digest: TrimmedNonEmptyString,
  layers: Schema.Array(TrimmedNonEmptyString),
  // The authority's own receipt (CHR14/CHR20): its sha256 over the compiled
  // text and the snapshot it froze for this thread. Optional because a Local
  // thread with nothing deposited has neither; persisted so cold recovery
  // carries them with the payload.
  authorityDigest: Schema.optional(TrimmedNonEmptyString),
  snapshotId: Schema.optional(TrimmedNonEmptyString),
});
export type ProviderSessionInjection = typeof ProviderSessionInjection.Type;

export const ProviderConversationHistory = Schema.Struct({
  revision: TrimmedNonEmptyString,
  messages: Schema.Array(
    Schema.Struct({
      role: Schema.Literals(["user", "assistant"]),
      text: Schema.String,
      attachments: Schema.optional(Schema.Array(ChatAttachment)),
    }),
  ),
});
export type ProviderConversationHistory = typeof ProviderConversationHistory.Type;

export const ProviderSessionStartInput = Schema.Struct({
  // Server-selected retained history; this replaces an older native continuation.
  initialHistory: Schema.optional(ProviderConversationHistory),
  localWorkPolicy: Schema.optional(LocalWorkPolicy),
  threadId: ThreadId,
  // The operating-mode discriminant (see resolveProviderOperatingMode):
  // null = the explicit Local harness; a key = governed World mode.
  worldKey: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  provider: Schema.optional(ProviderDriverKind),
  // See ProviderSession for the migration story.
  providerInstanceId: Schema.optional(ProviderInstanceId),
  cwd: Schema.optional(TrimmedNonEmptyString),
  modelSelection: Schema.optional(ModelSelection),
  resumeCursor: Schema.optional(Schema.Unknown),
  approvalPolicy: Schema.optional(ProviderApprovalPolicy),
  sandboxMode: Schema.optional(ProviderSandboxMode),
  runtimeMode: RuntimeMode,
  // The frozen layered doctrine for this session (absent when nothing is
  // authored). Server-computed at start; never sent by clients.
  sessionInjection: Schema.optional(ProviderSessionInjection),
  // BD6: the effective kind-row names frozen at Session start. Adapters may
  // only intersect their existing grant/tool surface with this list.
  toolProfile: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
});
export type ProviderSessionStartInput = typeof ProviderSessionStartInput.Type;

export const ProviderSendTurnInput = Schema.Struct({
  localWorkPolicy: Schema.optional(LocalWorkPolicy),
  offlineContextReceipt: Schema.optional(OfflineContextReceipt),
  // ProviderService supplies these bytes from the verified local store. A
  // client never selects or supplies the provider's prepared documents.
  offlineContextDocuments: Schema.optional(Schema.Array(OfflineCanvas)),
  threadId: ThreadId,
  input: Schema.optional(
    TrimmedNonEmptyString.check(Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_INPUT_CHARS)),
  ),
  attachments: Schema.optional(
    Schema.Array(ChatAttachment).check(Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_ATTACHMENTS)),
  ),
  modelSelection: Schema.optional(ModelSelection),
  interactionMode: Schema.optional(ProviderInteractionMode),
  // The frozen per-message boundary delivered to the provider leg (O17's
  // provider-input receipt). Optional for ungoverned/local turns.
  contextResolution: Schema.optional(MessageContextResolution),
  // A Steer: deliver this message into the turn that is running instead of
  // refusing the Send. `messageId` is the person's message, for engines that
  // echo a client id back on the steered item; `expectedTurnId` is the turn
  // the message was written into, so it never joins a later one.
  queueDelivery: Schema.optional(Schema.Literal("now")),
  messageId: Schema.optional(MessageId),
  expectedTurnId: Schema.optional(TurnId),
  // SERVER-ONLY. The recorded handoff (the conversation so far) that the
  // reactor delivers with a turn-starting Send; ProviderService puts its text
  // ahead of the input. This type is built by the server from the stored
  // `context.handoff` activity. It is never decoded from a client: the client
  // command (`ClientThreadTurnStartCommand`) has no such field and drops one
  // that is sent (provider.test.ts proves it).
  handoff: Schema.optional(
    Schema.Struct({
      id: TrimmedNonEmptyString,
      text: Schema.String.check(Schema.isMaxLength(PROVIDER_HANDOFF_MAX_TEXT_CHARS)),
    }),
  ),
  // W-ead01b93: a Live spoken hand-off, from its turn-start event. `input` is
  // the person's words alone (the Send attests only that); ProviderService
  // gives the model this call's earlier dialogue as marked context that is not
  // the person's request (liveVoiceMessage.ts liveHandoffTaskInput).
  liveHandoff: Schema.optional(LiveHandoff),
  // SERVER-ONLY. W-89a78759 (PR #1708): the person typed this Send in their own
  // Desk's composer, from its turn-start event. ProviderService asks the World
  // to record the mark beside the person's message digest (sendAttestation).
  personAuthorship: Schema.optional(PersonAuthorship),
});
export type ProviderSendTurnInput = typeof ProviderSendTurnInput.Type;

export const ProviderTurnStartResult = Schema.Struct({
  threadId: ThreadId,
  turnId: TurnId,
  resumeCursor: Schema.optional(Schema.Unknown),
});
export type ProviderTurnStartResult = typeof ProviderTurnStartResult.Type;

export const ProviderInterruptTurnInput = Schema.Struct({
  threadId: ThreadId,
  turnId: Schema.optional(TurnId),
});
export type ProviderInterruptTurnInput = typeof ProviderInterruptTurnInput.Type;

export const ProviderStopSessionInput = Schema.Struct({
  threadId: ThreadId,
});
export type ProviderStopSessionInput = typeof ProviderStopSessionInput.Type;

export const ProviderRespondToRequestInput = Schema.Struct({
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  decision: ProviderApprovalDecision,
});
export type ProviderRespondToRequestInput = typeof ProviderRespondToRequestInput.Type;

export const ProviderRespondToUserInputInput = Schema.Struct({
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  answers: ProviderUserInputAnswers,
});
export type ProviderRespondToUserInputInput = typeof ProviderRespondToUserInputInput.Type;

const ProviderEventKind = Schema.Literals(["session", "notification", "request", "error"]);

export const ProviderEvent = Schema.Struct({
  id: EventId,
  kind: ProviderEventKind,
  provider: ProviderDriverKind,
  // See ProviderSession for the migration story.
  providerInstanceId: Schema.optional(ProviderInstanceId),
  threadId: ThreadId,
  createdAt: IsoDateTime,
  method: TrimmedNonEmptyString,
  message: Schema.optional(TrimmedNonEmptyString),
  turnId: Schema.optional(TurnId),
  itemId: Schema.optional(ProviderItemId),
  requestId: Schema.optional(ApprovalRequestId),
  requestKind: Schema.optional(ProviderRequestKind),
  textDelta: Schema.optional(Schema.String),
  payload: Schema.optional(Schema.Unknown),
});
export type ProviderEvent = typeof ProviderEvent.Type;
